import "../styles/globals.css";
import { useEffect } from "react";
import type { AppProps } from "next/app";
import { QueryClientProvider } from "@tanstack/react-query";
import { useRouter } from "next/router";
import Head from "next/head";
import { Toaster } from "@/components/ui/sonner";
import { initMixpanel, trackPageView } from "@/lib/mixpanel-client";
import { queryClient } from "@/lib/tanstack/query-client";
import { CompanySettingsProvider } from "@/contexts/company-settings-context";
import { ErrorBoundary } from "@repo/ui/components/ui/error-boundary";

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();

  useEffect(() => {
    initMixpanel(); // Initialize Mixpanel

    const handleRouteChange = (url: string) => {
      trackPageView(url); // Track pageviews
    };

    // Track initial page load
    trackPageView(router.asPath);

    // Track page views on route change
    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events, router.asPath]);

  return (
    <QueryClientProvider client={queryClient}>
      <CompanySettingsProvider
        settings={{
          currency: "USD",
        }}
      >
        <Head>
          <title>Homeworks Client Portal</title>
          <meta
            name="description"
            content="Client portal for home service companies"
          />
          <meta
            name="robots"
            content="noindex, nofollow, noarchive, nosnippet, noimageindex"
          />
          <meta
            name="googlebot"
            content="noindex, nofollow, noarchive, nosnippet, noimageindex"
          />
        </Head>
        <div id="__app_root">
          <ErrorBoundary>
            <Component {...pageProps} />
            <Toaster />
          </ErrorBoundary>
        </div>
      </CompanySettingsProvider>
    </QueryClientProvider>
  );
}
